
// client/src/components/Raffle.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import './Raffle.css';

// Message banners sit on a .note surface (styles/surfaces.css) so their text
// stays dark-on-tint in every theme (theme-minimal painted it white).
const NOTE_VARIANT = { success: 'ok', error: 'error', warning: 'warn', info: 'info' };

const Raffle = ({ tenantId: tenantProp } = {}) => {
  // The tenant route segment is `:tenant` (TenantRoutes passes it as the
  // tenantId prop too). Reading a `tenantId` param here was always undefined,
  // so every request went to /api/raffle/undefined/… and the page never
  // found the raffle.
  const { tenant, eventId, itemId } = useParams();
  const tenantId = tenantProp || tenant;
  const baseUrl = process.env.REACT_APP_API_URL || '';

  const [raffleData, setRaffleData] = useState(null);
  const [winners, setWinners] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    loadRaffleData();
  }, [tenantId, eventId, itemId]);

  const loadRaffleData = async () => {
    setLoading(true);
    try {
      // Get raffle config
      const configResponse = await axios.get(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}`,
        { withCredentials: true }
      );

      if (configResponse.data.success && configResponse.data.raffle) {
        setRaffleData(configResponse.data.raffle);
      }

      // Get entries
      const entriesResponse = await axios.get(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/entries`,
        { withCredentials: true }
      );

      if (entriesResponse.data.success) {
        setEntries(entriesResponse.data.entries || []);
      }

      // Get winners if raffle is complete
      const winnersResponse = await axios.get(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/winners`,
        { withCredentials: true }
      );

      if (winnersResponse.data.success) {
        setWinners(winnersResponse.data.winners || []);
      }
    } catch (error) {
      console.error('Error loading raffle data:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Error loading raffle data', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRunRaffle = async () => {
    if (!raffleData) {
      setMessage({ text: 'Raffle not configured yet', type: 'error' });
      return;
    }

    const winnersPerCombo = raffleData.winners_per_combo || 1;
    
    if (!window.confirm(`Draw ${winnersPerCombo} winner(s) per attribute combination?`)) {
      return;
    }

    setDrawing(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await axios.post(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/draw`,
        { winnersPerCombo },
        { withCredentials: true }
      );

      if (response.data.success) {
        setMessage({ 
          text: `Successfully drew ${response.data.winners.length} winner(s)!`, 
          type: 'success' 
        });
        
        // Reload data to show winners
        await loadRaffleData();
      } else {
        setMessage({ 
          text: response.data.message || 'Failed to draw winners', 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error running raffle:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Error running raffle', 
        type: 'error' 
      });
    } finally {
      setDrawing(false);
    }
  };

  const formatAttributes = (attrs) => {
    if (!attrs || typeof attrs !== 'object') return 'N/A';
    return Object.entries(attrs)
      .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`)
      .join(', ');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'NEW': { label: 'Not Started', class: 'status-new' },
      'ACTIVE': { label: 'Active', class: 'status-active' },
      'COMPLETE': { label: 'Complete', class: 'status-complete' },
      'PROCESSING': { label: 'Processing...', class: 'status-processing' }
    };

    const statusInfo = statusMap[status] || { label: status, class: 'status-default' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  if (loading) {
    return (
      <div className="raffle-container">
        <div className="loading-message">Loading raffle data...</div>
      </div>
    );
  }

  if (!raffleData) {
    return (
      <div className="raffle-container">
        <div className="warning-message">
          <p>No raffle configuration found for this item.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="raffle-container">
      <div className="raffle-header">
        <h2>{raffleData.name || 'Raffle'}</h2>
        {getStatusBadge(raffleData.status)}
      </div>

      {message.text && (
        <div className={`message ${message.type} note ${NOTE_VARIANT[message.type] || 'info'}`}>
          {message.text}
        </div>
      )}

      <div className="raffle-info-grid">
        <div className="info-card">
          <h3>Configuration</h3>
          <div className="info-item">
            <span className="label">Winners per combo:</span>
            <span className="value">{raffleData.winners_per_combo}</span>
          </div>
          {raffleData.size_filter && (
            <div className="info-item">
              <span className="label">Size Filter:</span>
              <span className="value">{raffleData.size_filter}</span>
            </div>
          )}
          {raffleData.start_at && (
            <div className="info-item">
              <span className="label">Start:</span>
              <span className="value">{new Date(raffleData.start_at).toLocaleString()}</span>
            </div>
          )}
          {raffleData.end_at && (
            <div className="info-item">
              <span className="label">End:</span>
              <span className="value">{new Date(raffleData.end_at).toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="info-card">
          <h3>Statistics</h3>
          <div className="info-item">
            <span className="label">Total Entries:</span>
            <span className="value">{entries.length}</span>
          </div>
          <div className="info-item">
            <span className="label">Winners Selected:</span>
            <span className="value">{winners.length}</span>
          </div>
        </div>
      </div>

      {/* Draw Button */}
      {raffleData.status !== 'COMPLETE' && raffleData.status !== 'PROCESSING' && entries.length > 0 && (
        <div className="action-section">
          <button
            onClick={handleRunRaffle}
            disabled={drawing}
            className="btn btn-primary btn-large"
          >
            {drawing ? (
              <>
                <span className="spinner"></span> Drawing Winners...
              </>
            ) : (
              'Run Raffle'
            )}
          </button>
        </div>
      )}

      {raffleData.status === 'PROCESSING' && (
        <div className="info-message">
          <p>Raffle is currently being processed. Please wait...</p>
        </div>
      )}

      {raffleData.status === 'COMPLETE' && (
        <div className="complete-message">
          <p>✓ This raffle has been completed. Winners have been notified.</p>
        </div>
      )}

      {entries.length === 0 && (
        <div className="warning-message">
          <p>No entries yet. Share the raffle entry link to collect entries.</p>
        </div>
      )}

      {/* Winners Section */}
      {winners.length > 0 && (
        <div className="winners-section">
          <h3>🎉 Winners</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Attributes</th>
                  <th>Selected On</th>
                </tr>
              </thead>
              <tbody>
                {winners.map((winner) => (
                  <tr key={winner.id} className="winner-row">
                    <td><strong>{winner.name}</strong></td>
                    <td>{winner.email}</td>
                    <td>{formatAttributes(winner.attributes)}</td>
                    <td>{new Date(winner.created_on).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All Entries Section */}
      {entries.length > 0 && (
        <div className="entries-section">
          <h3>All Entries</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Attributes</th>
                  <th>Entry Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.name}</td>
                    <td>{entry.email}</td>
                    <td>{entry.phone || 'N/A'}</td>
                    <td>{formatAttributes(entry.attributes)}</td>
                    <td>{new Date(entry.created_on).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Raffle;